'use client';

import { useCtaResolver } from '@hyphengroup/cms-components/components/ApplyCta';
import CmsRewardCardGrid from '@hyphengroup/cms-components/components/RewardCardGrid';
import { usePathname } from 'next/navigation';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RewardCardGridDetailsModal } from './components/RewardCardGridDetailsModal';
import { RewardCardGridTranslations } from './i18n';
import type {
  RewardCardGridDetailsModalItem,
  RewardCardGridProps,
  RewardCardGridCardItem,
  RewardCardGridItem,
  RewardCardGridItemsContext,
} from './types';
import {
  buildRewardCardsViewUrl,
  buildRewardGridImpressionPayload,
  buildRewardTrackingPayload,
  getRewardGridItemIndex,
  getRewardGridCardProductPreviewItems,
  getRewardValueText,
  getUniqueProducts,
  mapProductToProductGridCardProps,
} from './utils';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

const useRewardCardGridItems = ({
  items,
  pathname,
  componentIndex,
  labels,
  onOpenDetailsModal,
  layoutType,
}: RewardCardGridItemsContext & {
  items: RewardCardGridItem[];
}): {
  cardItems: RewardCardGridCardItem[];
  productDetailModal: ReactNode;
} => {
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `RewardCardGrid_${componentIndex}`,
  });
  const resolve = useCtaResolver();

  const cardItems = useMemo(() => {
    return items
      .filter((item) => item.gift)
      .map((item, index) => {
        const uniqueProducts = getUniqueProducts(item.products);
        const giftName = item.gift?.gift_title ?? '';
        const rewardCampaignId = item.gift?.uid?.trim() || undefined;
        const rewardCampaignName = giftName || undefined;
        const itemIndex = getRewardGridItemIndex({
          index,
          layoutType,
        });
        const primaryCtaLabel =
          uniqueProducts.length > 0
            ? item.primaryCtaLabel || labels.primaryCtaLabel
            : undefined;
        const secondaryCtaLabel =
          item.secondaryCtaLabel || labels.secondaryCtaLabel;
        const rewardValueText = getRewardValueText(item);
        const rewardIdForUrl = rewardCampaignId || giftName.trim();
        const rewardCardsViewUrl = rewardIdForUrl
          ? buildRewardCardsViewUrl({
              pathname,
              rewardId: rewardIdForUrl,
            })
          : undefined;

        const productCards = uniqueProducts.map((product) => {
          const applyCtaUrl = resolve({ product });
          const canOpenPdpModal = Boolean(product.url_alias);

          return mapProductToProductGridCardProps({
            product,
            primaryCtaUrl: applyCtaUrl,
            secondaryCtaUrl: undefined,
            onSecondaryCtaClick: canOpenPdpModal
              ? () => {
                  openProductDetail(product, null);
                }
              : undefined,
          });
        });

        return {
          id: item.id,
          tagColor: item.colorTag,
          tagText:
            item.gift?.gift_value?.urgency_label ?? item.rewardValueLabel,
          imageUrl: item.gift?.desktop_image || item.gift?.mobile_image,
          imageAlt: giftName || 'Reward image',
          title: giftName,
          rewardValueLabel: item.rewardValueLabel,
          rewardValueText,
          rewardValueTooltip: item.gift?.additional_information,
          previewItems: getRewardGridCardProductPreviewItems({
            products: uniqueProducts,
          }),
          primaryCtaLabel,
          primaryCtaUrl:
            uniqueProducts.length > 0 ? rewardCardsViewUrl : undefined,
          onPrimaryCtaClick:
            uniqueProducts.length > 0 && rewardCardsViewUrl
              ? () => {
                  if (!primaryCtaLabel) return;
                  pushDataLayer(
                    buildRewardTrackingPayload({
                      componentTitle: `Reward Card Grid/${giftName}_${primaryCtaLabel}`,
                      componentIndex,
                      index: itemIndex,
                      rewardCampaignName,
                      rewardCampaignId,
                    }),
                  );
                }
              : undefined,
          secondaryCtaLabel,
          onSecondaryCtaClick: () => {
            pushDataLayer(
              buildRewardTrackingPayload({
                componentTitle: `Reward Card Grid/${giftName}_${secondaryCtaLabel}`,
                componentIndex,
                index: itemIndex,
                rewardCampaignName,
                rewardCampaignId,
              }),
            );

            onOpenDetailsModal({
              gift: item.gift,
              rewardValueLabel: item.rewardValueLabel,
              rewardValueText,
              products: productCards,
            });
          },
        } satisfies RewardCardGridCardItem;
      });
  }, [
    items,
    pathname,
    componentIndex,
    labels,
    onOpenDetailsModal,
    layoutType,
    openProductDetail,
    resolve,
  ]);

  return { cardItems, productDetailModal };
};

export const RewardCardGridComponent: React.FC<RewardCardGridProps> = ({
  items = [],
  layoutType = 'GRID',
  componentIndex = '',
}) => {
  const context = useClientContext();
  const pathname = usePathname() ?? '';
  const [selectedItem, setSelectedItem] =
    useState<RewardCardGridDetailsModalItem | null>(null);
  const hasTrackedImpressionRef = useRef(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const staticText = getClientTranslations(RewardCardGridTranslations, context);

  const openDetailsModal = useCallback(
    (item: RewardCardGridDetailsModalItem) => {
      setSelectedItem(item);
    },
    [],
  );

  const { cardItems, productDetailModal } = useRewardCardGridItems({
    items,
    pathname,
    componentIndex,
    labels: {
      primaryCtaLabel: staticText.seeEligibleCards,
      secondaryCtaLabel: staticText.rewardDetails,
    },
    onOpenDetailsModal: openDetailsModal,
    layoutType,
  });

  useEffect(() => {
    if (hasTrackedImpressionRef.current || !cardItems.length) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const section = sectionRef.current;
      if (!section || hasTrackedImpressionRef.current) {
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const isVisibleOnLoad =
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < viewportHeight &&
        rect.left < viewportWidth;

      if (!isVisibleOnLoad) {
        return;
      }

      hasTrackedImpressionRef.current = true;
      pushDataLayer(
        buildRewardGridImpressionPayload({
          componentIndex,
        }),
      );
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [cardItems.length, componentIndex]);

  if (!cardItems.length) {
    return null;
  }

  return (
    <>
      <section
        ref={sectionRef}
        className="w-full"
        data-layout-type={layoutType.toLowerCase()}
      >
        <CmsRewardCardGrid items={cardItems} layoutType={layoutType} />
      </section>

      <RewardCardGridDetailsModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
      {productDetailModal}
    </>
  );
};
